import { $httpFhirApi } from '@/common/api/httpFhir.service';
import timezoneApi from '@/common/api/timezone.api';
import { factList, printDate } from '@/components/questionnaires/widgets/print/printData';
import Consent from '@/fhirworks/Consent';

// The rules for a release of information (ROI) Consent that every surface showing one shares: the
// live form, its print view, the contact card and the form snapshot view.

export const consentStatusMap = {
    active: { color: 'success', text: 'Active' },
    proposed: { color: 'warning', text: 'Pending' },
    rejected: { color: 'error', text: 'Revoked' },
    inactive: { color: '', text: 'Inactive' },
};

// The status a release reads as. An active release past its end date has lapsed even while its
// stored status still says active. Accepts a Consent model or plain JSON.
export const consentDisplayStatus = (consent) => {
    if (!consent) return undefined;
    const expired = typeof consent.isExpired === 'function' ? consent.isExpired() : Consent.prototype.isExpired.call(consent);
    if (consent.status === 'active' && expired) return 'inactive';
    return consent.status;
};

const noActions = () => ({ addRelease: false, revoke: false, delete: false });

// What may be done to a release where it is shown. Printed output and the portal get no actions; a
// completed form is a legal record, so revoke (a status transition) is all it allows. Revoke is
// offered only while the release is still in effect.
export const roiActionPermissions = (consent, { printView = false, isPortal = false, isLocked = false } = {}) => {
    if (printView || isPortal) return noActions();
    const revoke = !!consent && consentDisplayStatus(consent) === 'active';
    if (isLocked) return { addRelease: false, revoke, delete: false };
    return { addRelease: true, revoke, delete: true };
};

export const REVOKE_REASON_REQUIRED = 'Enter the reason this release of information was revoked.';

// Who the release shares information with, the name a reader tells one release from another by.
export const releaseRecipient = (consent) => consent?.provision?.actor?.[0]?.reference?.display || '';

// A fresh copy each time, since the dialog writes `invalid` onto the options it is shown with. A
// caller away from the release itself names its recipient, so the question says which one.
export const revokeDialogOptions = (recipient = '') => ({
    header: 'Revoke release of information',
    warning: "A revoked release can't be made active again. It stops authorizing disclosure from now on.",
    confirm: 'Yes',
    cancel: 'No',
    type: 'text',
    label: 'Reason release of information was revoked',
    message: recipient ? `Are you sure you want to revoke this release of information with ${recipient}?` : 'Are you sure you want to revoke this release of information?',
    validate: true,
    invalid: REVOKE_REASON_REQUIRED,
});

// Who is revoking, and when in the user's own zone, as both revoke paths record it.
export const revocationStamp = (authStore) => ({
    who: {
        id: authStore.aidboxPersonResource?.id,
        resourceType: authStore.aidboxPersonResource?.resourceType,
        display: authStore.aidboxPersonReference?.display,
    },
    when: timezoneApi.getLocalDateTime(new Date(), "yyyy-MM-dd'T'HH:mm:ss.SSSxxx", Intl.DateTimeFormat().resolvedOptions().timeZone),
});

// Withdraw the release in place: Revoked, ended now, and who/when/why recorded. The reason is a
// string extension, so a blank one is left out rather than saved empty.
export const applyRevocation = (consent, { who, when, reason } = {}) => {
    const trimmed = typeof reason === 'string' ? reason.trim() : '';
    consent.status = 'rejected';
    consent.period = { start: consent.period?.start, end: new Date().toISOString() };
    consent.revoke = { who, when };
    if (trimmed) {
        consent.revoke.reason = trimmed;
    }
    return consent;
};

// The widget writes both marks when it revokes, so either alone is enough to say so.
const isRevoked = (consent) => consent?.status === 'rejected' || !!consent?.revoke?.when;

// Who withdrew the authorization, when, and why, as the printed record states it.
export const revocationFacts = (consent) => {
    if (!isRevoked(consent)) return [];
    return factList([
        { label: 'Revoked', value: printDate(consent.revoke?.when) || printDate(consent.period?.end) },
        { label: 'by', value: consent.revoke?.who?.display },
        { label: 'Reason', value: consent.revoke?.reason },
    ]);
};

// The roi item answer carries the response's consents as JSON, one object or an array of them.
// Unparseable reads as none, so a bad answer never newly blocks lock() or signReview().
export const parseRoiConsents = (roiItem) => {
    const answer = roiItem?.answer?.[0]?.value?.string;
    if (!answer) return [];

    try {
        const parsed = JSON.parse(answer);
        return Array.isArray(parsed) ? parsed : [parsed];
    } catch (error) {
        // Reading as none is deliberate, but a corrupt answer would otherwise leave no trace at all.
        console.error('[parseRoiConsents] unparseable roi answer; treating the response as recording no consents', error);
        return [];
    }
};

// The current Consents behind a set of releases, in one read. Throws on a failed read.
export const loadLiveConsents = async (ids) => {
    const unique = [...new Set((ids || []).filter(Boolean))];
    if (!unique.length) return [];
    const response = await $httpFhirApi.get(`Consent?_id=${unique.map(encodeURIComponent).join(',')}`);
    const data = response?.data;
    return Array.isArray(data) ? data : data?.entry?.map((entry) => entry.resource) || [];
};
